import React from "react";
import { FilloutStandardEmbed } from "@fillout/react";
import { Badge } from "./ui/badge";
import { FileCheck, Shield, Clock } from "lucide-react";
import { Fillout } from "@fillout/api";
import { useTranslation } from "react-i18next";

const FILLOUT_API_KEY = process.env.REACT_APP_FILLOUT_API_KEY;
const FILLOUT_FORM_ID = "b5xJ9xDUPHus";
const fillout = new Fillout(FILLOUT_API_KEY);

export const FormSection = () => {
  const { t } = useTranslation();

  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const trackLead = (payload) => {
    const hasCookie = document.cookie.includes("am_id");

    if (!window.affiliateManager) {
      return;
    }

    const trackData = {
      firstName: payload.fullName || "",
      lastName: "",
      uid: payload.uid || "",
      phone: payload.phone || ""
    };
    
    if (payload.email && payload.email.trim()) {
      trackData.email = payload.email.trim();
    }

    window.affiliateManager.trackLead(trackData, function () {
    });
  };

  const handleSubmit = async (submissionUuid) => {
    try {
      const data = await fillout.getSubmission(FILLOUT_FORM_ID, submissionUuid);
      
      let fullName = "";
      let email = "";
      let phone = "";

      const questions = data.questions || [];
      questions.forEach((q) => {
        const fieldName = (q.name || q.question || "").toLowerCase();
        const fieldValue = q.value || "";
        if (!fieldValue) return;

        const val = String(fieldValue).trim();

        if (
          ["nombre", "apellido", "name", "last name", "lastname"].some(k => fieldName.includes(k))
        ) {
          if (!fullName) {
            fullName = val;
          } else if (!fullName.toLowerCase().includes(val.toLowerCase())) {
            fullName += " " + val;
          }
        } 
        else if (["email", "correo", "e-mail"].some(k => fieldName.includes(k))) {
          email = val;
        } 
        else if (
          ["teléfono", "telefono", "phone", "móvil", "movil", "whatsapp", "tel"].some(k => fieldName.includes(k))
        ) {
          phone = val;
        }
      });

      trackLead({
        fullName: fullName,
        phone: phone,
        email: email,
        uid: data.submissionId || submissionUuid,
      });

      // GA4 conversion event
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'form',
          event_label: 'viabilidad_form',
        });
      }

      // Meta Pixel conversion event
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead');
      }

      setIsSubmitted(true);
    } catch (error) {
      trackLead({ uid: submissionUuid });

      // GA4 conversion event (fallback)
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'form',
          event_label: 'viabilidad_form',
        });
      }

      // Meta Pixel conversion event (fallback)
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead');
      }

      setIsSubmitted(true);
    }
  };

  React.useEffect(() => {
    if (isSubmitted) {
      if (window.__wbg_gam_pixel_fired) return;
      window.__wbg_gam_pixel_fired = true;

      const PIXEL_URL = "https://partner.googleadservices.com/gampad/ads?gct=KhCOnpPJg0wKZ4oYZCpYCAIQFEoAWICAgJDakrieF7gB-onFkIQE0gIJaW1hZ2UvcG5n8AIB-AIBkAMAyAMB0AMB8AMCygUXaHR0cHM6Ly9yZXNpZGVuY2lheWEuZXPqBgCaCAISALIBAhgBygECQAEYqYr8zAYoADCpprHUBjgAWAFqBl9ibGFua3De2vHMBg&iu=23273956658&gdfp_req=1&height=1&width=1&impl=ifr&sfv=1-0-45";

      const sep = PIXEL_URL.indexOf("?") >= 0 ? "&" : "?";
      const url = PIXEL_URL + sep + "cb=" + Date.now();

      const img = new Image(1, 1);
      img.src = url;
      img.style.cssText = "position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;";
      document.body.appendChild(img);
    }
  }, [isSubmitted]);

  return (
    <section className="py-20 lg:py-28 bg-white" id="viabilidad">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 section-reveal">
          <Badge className="bg-[#F5F1EA] text-[#C8A24D] border-[#E8D5A0] hover:bg-[#F5F1EA] mb-6 py-1.5 px-4 text-xs font-semibold tracking-wide uppercase">
            {t("form.badge")}
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-[42px] text-[#0C2340] leading-tight mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
            {t("form.title")}
          </h2>
          <p className="text-lg text-[#4A5568] leading-relaxed">
            {t("form.subtitle")}
          </p>
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap justify-center gap-6 mb-10 section-reveal">
          {[
            { icon: FileCheck, text: t("form.trust1") },
            { icon: Shield, text: t("form.trust2") },
            { icon: Clock, text: t("form.trust3") },
          ].map((item) => (
            <span key={item.text} className="flex items-center gap-2 text-sm text-[#4A5568]">
              <item.icon className="w-4 h-4 text-[#C8A24D]" />
              {item.text}
            </span>
          ))}
        </div>

        {/* Fillout Form Embed - Official React Component */}
        <div className="max-w-3xl mx-auto section-reveal">
          <div className="bg-[#FAFAF7] rounded-3xl border border-[#E8E4DC] overflow-hidden p-2 sm:p-4">
            <FilloutStandardEmbed
              filloutId="b5xJ9xDUPHus"
              domain="formulario.residenciaya.es"
              inheritParameters
              dynamicResize
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FormSection;
